import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import { DatePickerValue, Input } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import moment from 'moment-timezone'

import i18n from 'modules/core/lib/i18n'

export type EventDetails = {
  location: string
  startsAt: string
}

interface EventDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (details: EventDetails) => void
  onRemove: () => void
  initialDetails?: EventDetails
  hasEvent: boolean
}

function EventDetailsModal({
  isOpen,
  onClose,
  onSave,
  onRemove,
  initialDetails,
  hasEvent,
}: EventDetailsModalProps) {
  const userTimezone = moment.tz.guess()

  const [location, setLocation] = useState(initialDetails?.location ?? '')
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDetails?.startsAt ? moment(initialDetails.startsAt).toDate() : new Date()
  )
  const [selectedTime, setSelectedTime] = useState(
    initialDetails?.startsAt
      ? moment(initialDetails.startsAt).format('HH:mm')
      : moment().format('HH:mm')
  )

  useEffect(() => {
    if (isOpen) {
      setLocation(initialDetails?.location ?? '')
      setSelectedDate(
        initialDetails?.startsAt ? moment(initialDetails.startsAt).toDate() : new Date()
      )
      setSelectedTime(
        initialDetails?.startsAt
          ? moment(initialDetails.startsAt).format('HH:mm')
          : moment().format('HH:mm')
      )
    }
  }, [isOpen])

  const isSaveDisabled = !location.trim() || !selectedDate || !selectedTime

  const handleDateChange = (date: DatePickerValue | undefined) => {
    if (date?.value?.from) {
      setSelectedDate(date.value.from)
    }
  }

  const handleSave = () => {
    const [hours, minutes] = selectedTime.split(':')
    if (!hours || !minutes) return

    const startsAtMoment = moment(selectedDate)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)

    onSave({
      location,
      startsAt: startsAtMoment.toISOString(),
    })
    onClose()
  }

  const handleRemove = () => {
    onRemove()
    onClose()
  }

  const datePickerValue = useMemo(
    () => ({
      granularity: 'day' as const,
      value: { from: selectedDate, to: selectedDate },
    }),
    [selectedDate]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      width="sm"
      onClose={onClose}
      title={i18n.t('posts.communities.event_details.title')}
      primaryAction={
        hasEvent
          ? [
              {
                value: 'save',
                label: i18n.t('common.save'),
                onClick: handleSave,
                disabled: isSaveDisabled,
              },
              {
                value: 'remove',
                label: i18n.t('posts.communities.event_details.remove_event_details'),
                onClick: handleRemove,
                icon: Delete,
              },
            ]
          : {
              label: i18n.t('common.save'),
              onClick: handleSave,
              disabled: isSaveDisabled,
            }
      }
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16">
        <Input
          value={location}
          label={i18n.t('posts.communities.event_details.location')}
          placeholder={i18n.t('posts.communities.event_location_placeholder')}
          onChange={setLocation}
        />

        <Box flexDirection="row" gap="s16">
          <Box style={{ flex: '1' }}>
            <F0DatePicker
              label={i18n.t('posts.communities.event_details.date')}
              onChange={handleDateChange}
              size="sm"
              value={datePickerValue}
              minDate={new Date()}
            />
          </Box>

          <Box flexDirection="row" alignItems="flexEnd" gap="s8" style={{ flex: '1' }}>
            <Box style={{ flex: '1' }}>
              <Input
                label={i18n.t('posts.communities.event_details.time')}
                type="time"
                value={selectedTime}
                onChange={setSelectedTime}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default EventDetailsModal
